<template>
<Teleport to="body">
    <Transition
        name="welcome-modal"
        appear
        @after-leave="finishClosing"
    >
        <div
            v-if="visible"
            class="welcome-overlay"
            @click.self="dismiss"
        >
            <section
                ref="dialog"
                class="welcome-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby="welcome-dialog-title"
                aria-describedby="welcome-dialog-description welcome-dialog-profile"
                tabindex="-1"
            >
                <h1 id="welcome-dialog-title">Добро пожаловать!</h1>
                <p id="welcome-dialog-description" class="welcome-copy">
                    Рад видеть вас в Shinai! Я самостоятельно спроектировал и разработал этот сайт.
                    В нём я объединил продуманный интерфейс, удобный пользовательский опыт
                    и внимание к деталям.
                </p>
                <p id="welcome-dialog-profile" class="welcome-profile">
                    <a
                        href="https://github.com/res138/"
                        target="_blank"
                        rel="noopener noreferrer"
                    >Мой аккаунт на гитхаб<span class="welcome-sr-only"> (откроется в новой вкладке)</span></a>
                </p>

                <button
                    ref="primaryAction"
                    type="button"
                    class="welcome-action"
                    @click="dismiss"
                >
                    Хорошо
                </button>
            </section>
        </div>
    </Transition>
</Teleport>
</template>

<script>
import { rememberWelcome, shouldShowWelcome } from '@/services/welcomeVisit.js'

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',')

export default {
    name: 'WelcomeModal',
    data() {
        return {
            visible: shouldShowWelcome(),
            previouslyFocusedElement: null,
            active: false,
        }
    },
    watch: {
        visible(value, previousValue) {
            if (value && !previousValue) this.open()
        },
    },
    mounted() {
        if (this.visible) this.open()
    },
    beforeUnmount() {
        this.finishClosing(false)
    },
    methods: {
        open() {
            if (this.active) return
            this.active = true
            this.previouslyFocusedElement = document.activeElement
            document.body.classList.add('welcome-modal-open')
            window.addEventListener('keydown', this.handleKeydown, true)
            this.$nextTick(() => {
                this.$refs.primaryAction?.focus({ preventScroll: true })
            })
        },
        dismiss() {
            if (!this.visible) return
            rememberWelcome()
            this.visible = false
        },
        finishClosing(restoreFocus = true) {
            window.removeEventListener('keydown', this.handleKeydown, true)
            document.body.classList.remove('welcome-modal-open')
            if (
                this.active
                && restoreFocus
                && this.previouslyFocusedElement
                && typeof this.previouslyFocusedElement.focus === 'function'
            ) {
                this.previouslyFocusedElement.focus({ preventScroll: true })
            }
            this.active = false
            this.previouslyFocusedElement = null
        },
        focusableElements() {
            const dialog = this.$refs.dialog
            if (!dialog) return []
            return [...dialog.querySelectorAll(FOCUSABLE_SELECTOR)]
                .filter((element) => !element.hasAttribute('disabled') && element.tabIndex >= 0)
        },
        handleKeydown(event) {
            if (event.key === 'Escape' || Number(event.keyCode) === 461) {
                event.preventDefault()
                event.stopPropagation()
                this.dismiss()
                return
            }
            if (event.key !== 'Tab') return

            const dialog = this.$refs.dialog
            const elements = this.focusableElements()
            if (!dialog || !elements.length) {
                event.preventDefault()
                dialog?.focus({ preventScroll: true })
                return
            }

            const first = elements[0]
            const last = elements[elements.length - 1]
            const focusOutside = document.activeElement === dialog
                || !dialog.contains(document.activeElement)
            if (event.shiftKey && (document.activeElement === first || focusOutside)) {
                event.preventDefault()
                last.focus({ preventScroll: true })
            } else if (!event.shiftKey && (document.activeElement === last || focusOutside)) {
                event.preventDefault()
                first.focus({ preventScroll: true })
            }
        },
    },
}
</script>

<style scoped>
.welcome-overlay {
    position: fixed;
    z-index: 12000;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    inset: 0;
    display: grid;
    place-items: center;
    box-sizing: border-box;
    padding: 20px;
    padding: max(20px, env(safe-area-inset-top, 0px)) max(20px, env(safe-area-inset-right, 0px)) max(20px, env(safe-area-inset-bottom, 0px)) max(20px, env(safe-area-inset-left, 0px));
    background: rgba(0, 0, 0, .8);
    -webkit-backdrop-filter: blur(9px);
    backdrop-filter: blur(9px);
    font-family: Inter, Arial, sans-serif;
}

.welcome-dialog {
    position: relative;
    box-sizing: border-box;
    max-width: 448px;
    width: min(448px, calc(100vw - 40px));
    max-height: calc(100vh - 40px);
    max-height: calc(100dvh - 40px);
    overflow: auto;
    padding: 38px;
    border: 1px solid rgba(255, 255, 255, .13);
    border-radius: 20px;
    background: #111;
    color: #fff;
    box-shadow: 0 32px 100px rgba(0, 0, 0, .72);
}

.welcome-dialog:focus {
    outline: none;
}

.welcome-dialog h1 {
    margin: 0;
    font-family: Inter, Arial, sans-serif;
    font-size: 37px;
    font-size: clamp(26px, 4vw, 37px);
    font-weight: 750;
    line-height: 1.08;
    letter-spacing: -.04em;
}

.welcome-copy {
    margin: 18px 0 0;
    color: rgba(255, 255, 255, .72);
    font-size: 16px;
    font-weight: 400;
    line-height: 1.7;
}

.welcome-profile {
    margin: 10px 0 0;
    font-size: 15px;
    line-height: 1.5;
}

.welcome-profile a {
    color: #fff;
    font-weight: 650;
    text-decoration-color: #e31b23;
    text-decoration-thickness: 2px;
    text-underline-offset: 4px;
    transition: color .2s ease;
}

.welcome-profile a:hover {
    color: #ff7676;
}

.welcome-action {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    min-height: 52px;
    margin-top: 24px;
    padding: 0 20px;
    border: 0;
    border-radius: 14px;
    background: linear-gradient(135deg, #d40000 0%, #ff3e3e 100%);
    color: #fff;
    box-shadow: none;
    cursor: pointer;
    font: 700 15px/1 Inter, Arial, sans-serif;
    transition: filter .2s ease, transform .2s ease;
}

.welcome-action:hover {
    filter: brightness(1.08);
    transform: translateY(-1px);
}

.welcome-action:focus-visible,
.welcome-profile a:focus-visible {
    outline: 2px solid #fff;
    outline-offset: 4px;
}

.welcome-sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.welcome-modal-enter-active,
.welcome-modal-leave-active {
    transition: opacity .3s ease;
}

.welcome-modal-enter-active .welcome-dialog,
.welcome-modal-leave-active .welcome-dialog {
    transition: opacity .3s ease, transform .38s cubic-bezier(.2, .8, .2, 1);
}

.welcome-modal-enter-from,
.welcome-modal-leave-to {
    opacity: 0;
}

.welcome-modal-enter-from .welcome-dialog,
.welcome-modal-leave-to .welcome-dialog {
    opacity: 0;
    transform: translateY(18px) scale(.97);
}

@media (max-width: 600px) {
    .welcome-overlay {
        align-items: end;
        justify-items: stretch;
        padding: 0;
    }

    .welcome-dialog {
        width: 100%;
        max-width: none;
        max-height: calc(100vh - 24px);
        max-height: calc(100dvh - 24px);
        padding: 26px 20px 20px;
        padding-bottom: max(20px, env(safe-area-inset-bottom, 0px));
        overscroll-behavior: contain;
        border-bottom: 0;
        border-top: 3px solid #e31b23;
        border-radius: 24px 24px 0 0;
        background: #111;
        box-shadow: 0 -24px 70px rgba(0, 0, 0, .58);
    }

    .welcome-dialog h1 {
        margin: 0 -20px;
        padding: 0 20px 20px;
        border-bottom: 1px solid rgba(255, 255, 255, .1);
        font-size: 27px;
        text-align: center;
    }

    .welcome-copy {
        margin-top: 22px;
        font-size: 16px;
        line-height: 1.55;
    }

    .welcome-action {
        margin-top: 22px;
    }

    .welcome-modal-enter-active .welcome-dialog,
    .welcome-modal-leave-active .welcome-dialog {
        transition: transform .34s cubic-bezier(.22, 1, .36, 1);
    }

    .welcome-modal-enter-from .welcome-dialog,
    .welcome-modal-leave-to .welcome-dialog {
        opacity: 1;
        transform: translate3d(0, 100%, 0);
    }
}

@media (prefers-reduced-motion: reduce) {
    .welcome-modal-enter-active,
    .welcome-modal-leave-active,
    .welcome-modal-enter-active .welcome-dialog,
    .welcome-modal-leave-active .welcome-dialog,
    .welcome-action,
    .welcome-profile a {
        transition: none;
    }
}
</style>

<style>
body.welcome-modal-open {
    overflow: hidden;
}

.performance-lite .welcome-overlay,
.platform-webos .welcome-overlay {
    -webkit-backdrop-filter: none;
    backdrop-filter: none;
}
</style>
